import React, { useState } from 'react';
import {
    Dialog,
    DialogHeader,
    DialogBody,
    DialogFooter,
    Button,
    Typography,
    Chip,
    Card
} from '@material-tailwind/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faCheckCircle, faTimesCircle, faArrowLeft, faPencilAlt, faPlus } from '@fortawesome/free-solid-svg-icons';

const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-KE', {
        style: 'currency',
        currency: 'KES'
    }).format(amount || 0);

const formatDateTime = (date) => {
    if (!date) return 'N/A';
    const parsed = new Date(date);
    if (Number.isNaN(parsed.getTime())) return 'N/A';
    return parsed.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });
};

const getApprovalStatusColor = (status) => {
    switch (status) {
        case 'APPROVED':
            return 'green';
        case 'REJECTED':
            return 'red';
        case 'PENDING':
            return 'amber';
        default:
            return 'gray';
    }
};

const getReimbursementStatusColor = (status) => {
    switch (status) {
        case 'PAID':
            return 'green';
        case 'CANCELLED':
            return 'red';
        case 'PENDING':
            return 'orange';
        default:
            return 'gray';
    }
};

const Section = ({ title, children, muted = false }) => (
    <section className={`px-5 py-5 ${muted ? 'bg-gray-50/80' : ''}`}>
        <Typography variant="small" className="mb-4 text-[11px] font-bold uppercase tracking-[0.16em] text-gray-500">
            {title}
        </Typography>
        {children}
    </section>
);

const DetailGrid = ({ children }) => (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {children}
    </div>
);

const DetailItem = ({ label, value, wide = false }) => (
    <div className={wide ? 'sm:col-span-2 lg:col-span-3' : ''}>
        <Typography variant="small" className="text-gray-600">
            {label}
        </Typography>
        <Typography variant="small" className="font-semibold text-gray-900 break-words text-[13px] leading-6">
            {value || 'N/A'}
        </Typography>
    </div>
);

const ActionButtons = ({ expense, onApprovalAction, onReimbursementAction, onClose }) => {
    const [submitting, setSubmitting] = useState(false);
    const [actionError, setActionError] = useState('');

    const handleApproval = async (status, promptText) => {
        const notes = prompt(promptText);
        if (status === 'REJECTED' && !notes) {
            setActionError('Rejection reason is required');
            return;
        }

        setActionError('');
        setSubmitting(true);
        try {
            await onApprovalAction(expense.expense_id, status, notes || '');
            onClose?.();
        } catch (err) {
            const msg = err.response?.data?.message || err.message || 'Failed to update expense';
            setActionError(msg);
        } finally {
            setSubmitting(false);
        }
    };

    const handleMarkPaid = async () => {
        const notes = prompt('Enter reimbursement notes (optional):');
        setActionError('');
        setSubmitting(true);
        try {
            await onReimbursementAction(expense.expense_id, 'PAID', notes || '');
            onClose?.();
        } catch (err) {
            const msg = err.response?.data?.message || err.message || 'Failed to mark reimbursement as paid';
            setActionError(msg);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="space-y-3">
            {(expense.approval_status === 'PENDING' || (expense.requires_reimbursement && expense.reimbursement_status === 'PENDING')) && (
                <div className="flex flex-wrap gap-2">
                    {expense.approval_status === 'PENDING' && (
                        <>
                            <Button color="green" onClick={() => handleApproval('APPROVED', 'Enter approval notes (optional):')} disabled={submitting}>
                                <FontAwesomeIcon icon={faCheckCircle} className="mr-2" />
                                {submitting ? 'Processing...' : 'Approve'}
                            </Button>
                            <Button color="red" onClick={() => handleApproval('REJECTED', 'Enter rejection reason:')} disabled={submitting}>
                                <FontAwesomeIcon icon={faTimesCircle} className="mr-2" />
                                {submitting ? 'Processing...' : 'Reject'}
                            </Button>
                        </>
                    )}
                    {expense.requires_reimbursement && expense.reimbursement_status === 'PENDING' && (
                        <Button color="green" onClick={handleMarkPaid} disabled={submitting}>
                            {submitting ? 'Processing...' : 'Mark as Paid'}
                        </Button>
                    )}
                </div>
            )}

            {actionError && <div className="text-sm text-red-600">{actionError}</div>}
        </div>
    );
};

const ExpenseDetailContent = ({ expense, onApprovalAction, onReimbursementAction, onClose, onEdit, asPage = false }) => {
    if (!expense) return null;

    const content = (
        <Card className="overflow-hidden rounded-none border border-gray-200 bg-white shadow-none">
            <div className="divide-y divide-gray-200">
                <Section title="Basic Information">
                    <DetailGrid>
                        <DetailItem label="Expense Number" value={expense.expense_no} />
                        <div>
                            <Typography variant="small" className="text-gray-600">
                                Expense Type
                            </Typography>
                            <Chip
                                value={expense.expense_type}
                                color={expense.expense_type === 'VEHICLE' ? 'blue' : 'purple'}
                                size="sm"
                                className="mt-1"
                            />
                        </div>
                        <DetailItem label="Expense Date" value={formatDateTime(expense.expense_date)} />
                        <DetailItem label="Amount" value={formatCurrency(expense.amount)} />
                        <DetailItem label="Week" value={`Week ${expense.expense_week}, ${expense.expense_year}`} />
                        <DetailItem label="Paid By" value={expense.paid_by || 'N/A'} />
                    </DetailGrid>
                </Section>

                {Array.isArray(expense.line_items) && expense.line_items.length > 0 ? (
                    <Section title="Expense Line Items">
                        <div className="overflow-x-auto">
                            <table className="w-full border-collapse text-xs min-w-[500px]">
                                <thead>
                                    <tr className="bg-gray-50 border-b-2 border-gray-200">
                                        <th className="px-3 py-2 text-left font-semibold text-gray-600">#</th>
                                        <th className="px-3 py-2 text-left font-semibold text-gray-600">Type</th>
                                        <th className="px-3 py-2 text-left font-semibold text-gray-600">Vehicle</th>
                                        <th className="px-3 py-2 text-left font-semibold text-gray-600">Category</th>
                                        <th className="px-3 py-2 text-left font-semibold text-gray-600">Sub-category</th>
                                        <th className="px-3 py-2 text-right font-semibold text-gray-600">Amount (KES)</th>
                                        <th className="px-3 py-2 text-left font-semibold text-gray-600">Description</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {expense.line_items.map((li, idx) => (
                                        <tr key={li.id || idx} className="border-b border-gray-100">
                                            <td className="px-3 py-2 text-gray-400 font-semibold">{idx + 1}</td>
                                            <td className="px-3 py-2 text-gray-700">{li.expense_type}</td>
                                            <td className="px-3 py-2 text-gray-700">{li.plate_no || '—'}</td>
                                            <td className="px-3 py-2 text-gray-700">{li.category_name || li.category_id}</td>
                                            <td className="px-3 py-2 text-gray-500">{li.sub_category_name || '—'}</td>
                                            <td className="px-3 py-2 text-right font-semibold text-gray-800">
                                                {parseFloat(li.amount).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                                            </td>
                                            <td className="px-3 py-2 text-gray-600">{li.description}</td>
                                        </tr>
                                    ))}
                                    <tr className="bg-orange-50 border-t-2 border-orange-200">
                                        <td colSpan={5} className="px-3 py-2 text-right font-bold text-gray-700">Total</td>
                                        <td className="px-3 py-2 text-right font-bold text-[#FF5733]">
                                            {parseFloat(expense.amount).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                                        </td>
                                        <td></td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </Section>
                ) : (
                    <>
                        <Section title="Category Information">
                            <DetailGrid>
                                <DetailItem label="Category" value={expense.category_name} />
                                {expense.sub_category_name && <DetailItem label="Sub-Category" value={expense.sub_category_name} />}
                                <DetailItem label="Approval Required" value={expense.requires_approval ? 'Yes' : 'No'} />
                            </DetailGrid>
                        </Section>

                        {expense.expense_type === 'VEHICLE' && (
                            <Section title="Vehicle Information">
                                <DetailGrid>
                                    <DetailItem label="Vehicle" value={expense.plate_no} />
                                    <DetailItem label="Driver" value={expense.driver_name} />
                                    <DetailItem label="Owner" value={expense.owner_name} />
                                </DetailGrid>
                            </Section>
                        )}
                    </>
                )}

                <Section title="Payment Information">
                    <DetailGrid>
                        <DetailItem label="Payment Method" value={expense.payment_method || 'N/A'} />
                        <DetailItem label="Payment Reference" value={expense.payment_ref} />
                        <DetailItem label="Invoice Number" value={expense.invoice_number || expense.receipt_number} />
                        <DetailItem label="Invoice Date" value={formatDateTime(expense.invoice_date)} />
                    </DetailGrid>
                </Section>

                {expense.supplier_id && (
                    <Section title="Vendor Information">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <Typography variant="small" className="text-gray-600">
                                    Vendor Code
                                </Typography>
                                <a
                                    href={`/vendors/${expense.supplier_id}/ledger`}
                                    className="font-semibold text-[13px] leading-6 break-words text-orange-600 hover:text-orange-700 underline"
                                >
                                    {expense.supplier_code}
                                </a>
                            </div>
                            <div>
                                <Typography variant="small" className="text-gray-600">
                                    Vendor Name
                                </Typography>
                                <a
                                    href={`/vendors/${expense.supplier_id}/ledger`}
                                    className="font-semibold text-[13px] leading-6 break-words text-orange-600 hover:text-orange-700 underline"
                                >
                                    {expense.supplier_name_db}
                                </a>
                            </div>
                        </div>
                    </Section>
                )}

                {!expense.supplier_id && (expense.vendor_name || expense.vendor_phone) && (
                    <Section title="Vendor Information">
                        <DetailGrid>
                            <DetailItem label="Vendor Name" value={expense.vendor_name} />
                            <DetailItem label="Vendor Phone" value={expense.vendor_phone} />
                            <DetailItem label="Vendor Location" value={expense.supplier_location} />
                        </DetailGrid>
                    </Section>
                )}

                <Section title="Description & Notes">
                    <DetailGrid>
                        <DetailItem label="Description" value={expense.description} wide />
                        <DetailItem label="Additional Notes" value={expense.notes} wide />
                    </DetailGrid>
                </Section>

                <Section title="Approval Information">
                    <DetailGrid>
                        <div>
                            <Typography variant="small" className="text-gray-600">
                                Status
                            </Typography>
                            <div className="flex items-center gap-2 mt-1">
                                <Chip value={expense.approval_status} color={getApprovalStatusColor(expense.approval_status)} size="sm" />
                                {expense.is_reversed && (
                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-bold bg-gray-200 text-gray-700">
                                        REVERSED
                                    </span>
                                )}
                            </div>
                        </div>
                        <DetailItem label="Approved By" value={expense.approved_by_name} />
                        <DetailItem label="Approval Date" value={formatDateTime(expense.approval_date)} />
                        {expense.approval_notes && (
                            <DetailItem label="Approval Notes" value={expense.approval_notes} wide />
                        )}
                    </DetailGrid>
                </Section>

                {expense.requires_reimbursement && (
                    <Section title="Reimbursement Information">
                        <DetailGrid>
                            <div>
                                <Typography variant="small" className="text-gray-600">
                                    Status
                                </Typography>
                                <Chip
                                    value={expense.reimbursement_status}
                                    color={getReimbursementStatusColor(expense.reimbursement_status)}
                                    size="sm"
                                    className="mt-1"
                                />
                            </div>
                            <DetailItem label="Reimbursed By" value={expense.reimbursed_by_name} />
                            <DetailItem label="Reimbursement Date" value={formatDateTime(expense.reimbursement_date)} />
                            <DetailItem label="Reimbursement Notes" value={expense.reimbursement_notes} wide />
                        </DetailGrid>
                    </Section>
                )}

                {expense.is_recurring && (
                    <Section title="Recurring Information">
                        <DetailGrid>
                            <DetailItem label="Frequency" value={expense.recurring_frequency} />
                            <DetailItem label="End Date" value={formatDateTime(expense.recurring_end_date)} />
                        </DetailGrid>
                    </Section>
                )}

                <Section title="System Information" muted>
                    <DetailGrid>
                        <DetailItem label="Created By" value={expense.created_by_name || 'N/A'} />
                        <DetailItem label="Created At" value={formatDateTime(expense.created_at)} />
                        <DetailItem label="Last Updated" value={formatDateTime(expense.updated_at)} />
                        <DetailItem label="Discord Message ID" value={expense.discord_message_id} />
                        <DetailItem label="Expense ID" value={expense.id} />
                    </DetailGrid>
                </Section>

                {(onApprovalAction || onReimbursementAction) && (
                    <div className="px-5 py-5 bg-white">
                        <ActionButtons
                            expense={expense}
                            onApprovalAction={onApprovalAction}
                            onReimbursementAction={onReimbursementAction}
                            onClose={onClose}
                        />
                    </div>
                )}
            </div>
        </Card>
    );

    if (asPage) {
        return content;
    }

    return (
        <Dialog open={true} handler={onClose} size="lg" className="max-h-[90vh] overflow-y-auto">
            <DialogHeader className="border-b border-gray-200">
                <div className="flex items-center justify-between w-full gap-4">
                    <div>
                        <Typography variant="h5" className="text-gray-800">
                            Expense Details
                        </Typography>
                        <Typography variant="small" className="text-gray-500">
                            {expense.expense_no}
                        </Typography>
                    </div>
                    <Chip value={expense.approval_status} color={getApprovalStatusColor(expense.approval_status)} />
                </div>
            </DialogHeader>

            <DialogBody className="overflow-y-auto max-h-[calc(90vh-200px)]">
                {content}
            </DialogBody>

            <DialogFooter className="border-t border-gray-200 flex justify-between">
                <Button variant="outlined" onClick={onClose}>
                    <FontAwesomeIcon icon={faTimes} className="mr-2" />
                    Close
                </Button>
            </DialogFooter>
        </Dialog>
    );
};

const ExpenseDetails = (props) => {
    const { expense, asPage = false, onClose, onBack, onEdit, onDelete, onReverse, onCreateNew } = props;
    if (!expense) return null;

    if (asPage) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-[#fff8f5] to-[#EDE4DA]">
                <div className="w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                    <div className="mb-6 flex flex-col gap-4 border-b border-gray-200 pb-4 md:flex-row md:items-end md:justify-between">
                        <div className="min-w-0">
                            <p className="text-sm font-semibold uppercase tracking-[0.2em] text-[#FF5733]">Expense Record</p>
                            <h1 className="mt-2 text-3xl font-bold text-[#2a2e30] md:text-4xl">{expense.expense_no}</h1>
                            <p className="mt-2 text-sm text-gray-600">
                                {expense.line_items_count > 1
                                    ? `Multiple (${expense.line_items_count} items)`
                                    : `${expense.category_name}${expense.sub_category_name ? ` · ${expense.sub_category_name}` : ''}`
                                }
                            </p>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            <Button variant="outlined" onClick={onBack || onClose} className="flex items-center gap-2">
                                <FontAwesomeIcon icon={faArrowLeft} />
                                Back to list
                            </Button>
                            {onCreateNew && (
                                <Button variant="outlined" onClick={onCreateNew} className="flex items-center gap-2 border-[#FF5733] text-[#FF5733]">
                                    <FontAwesomeIcon icon={faPlus} />
                                    New Expense
                                </Button>
                            )}
                            {onEdit && (
                                <Button className="bg-[#FF5733] hover:bg-[#ff4520] flex items-center gap-2" onClick={() => onEdit(expense)}>
                                    <FontAwesomeIcon icon={faPencilAlt} />
                                    Edit Expense
                                </Button>
                            )}
                            {onReverse && (
                                <button
                                    onClick={onReverse}
                                    className="inline-flex items-center gap-2 rounded-lg border border-orange-300 bg-orange-50 px-3 py-1.5 text-sm font-medium text-orange-700 hover:bg-orange-100"
                                >
                                    Reverse
                                </button>
                            )}
                            {onDelete && (
                                <button
                                    onClick={onDelete}
                                    className="inline-flex items-center gap-2 rounded-lg border border-red-300 bg-red-50 px-3 py-1.5 text-sm font-medium text-red-700 hover:bg-red-100"
                                >
                                    Delete
                                </button>
                            )}
                        </div>
                    </div>

                    <ExpenseDetailContent {...props} asPage />
                </div>
            </div>
        );
    }

    return (
        <ExpenseDetailContent
            {...props}
            onClose={onClose}
        />
    );
};

export default ExpenseDetails;
